import { useCallback, useEffect, useRef } from 'react';
import { IDLE_SESSION_CONFIG } from '@/config/idleSessionConfig';

/**
 * Message types for cross-tab communication.
 */
type CrossTabMessage =
  | { type: 'activity'; timestamp: number }
  | { type: 'logout' }
  | { type: 'extend' }
  | { type: 'warning'; timeRemaining: number };

/**
 * Hook for cross-tab synchronization of idle session state.
 *
 * Uses BroadcastChannel API with localStorage fallback for older browsers.
 *
 * @returns Functions to broadcast events and register listeners
 */
export const useCrossTabSync = () => {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const activityCallbackRef = useRef<((timestamp: number) => void) | null>(null);
  const logoutCallbackRef = useRef<(() => void) | null>(null);
  const warningCallbackRef = useRef<((timeRemaining: number) => void) | null>(null);

  /**
   * Initialize BroadcastChannel if supported.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if ('BroadcastChannel' in window) {
      try {
        channelRef.current = new BroadcastChannel(IDLE_SESSION_CONFIG.BROADCAST_CHANNEL);

        channelRef.current.onmessage = (event: MessageEvent<CrossTabMessage>) => {
          handleMessage(event.data);
        };
      } catch (e) {
        console.warn('BroadcastChannel not available, using localStorage fallback');
      }
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === `${IDLE_SESSION_CONFIG.BROADCAST_CHANNEL}_message`) {
        try {
          const message = JSON.parse(event.newValue || '');
          handleMessage(message);
        } catch (e) {
          // Invalid message, ignore
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  /**
   * Handle incoming message from other tabs.
   */
  const handleMessage = useCallback((message: CrossTabMessage) => {
    switch (message.type) {
      case 'activity':
        if (activityCallbackRef.current) {
          activityCallbackRef.current(message.timestamp);
        }
        break;
      case 'logout':
        if (logoutCallbackRef.current) {
          logoutCallbackRef.current();
        }
        break;
      case 'extend':
        if (activityCallbackRef.current) {
          activityCallbackRef.current(Date.now());
        }
        break;
      case 'warning':
        if (warningCallbackRef.current) {
          warningCallbackRef.current(message.timeRemaining);
        }
        break;
    }
  }, []);

  /**
   * Broadcast a message to other tabs.
   */
  const broadcast = useCallback((message: CrossTabMessage) => {
    if (typeof window === 'undefined') return;

    if (channelRef.current) {
      try {
        channelRef.current.postMessage(message);
      } catch (e) {
        // Channel might be closed, fall through to localStorage
      }
    }

    try {
      const key = `${IDLE_SESSION_CONFIG.BROADCAST_CHANNEL}_message`;
      localStorage.setItem(key, JSON.stringify({ ...message, _ts: Date.now() }));
      setTimeout(() => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore
        }
      }, 100);
    } catch (e) {
      // localStorage might be unavailable
    }
  }, []);

  /**
   * Broadcast activity event to other tabs.
   */
  const broadcastActivity = useCallback(
    (timestamp: number) => {
      broadcast({ type: 'activity', timestamp });
    },
    [broadcast],
  );

  /**
   * Broadcast logout event to other tabs.
   */
  const broadcastLogout = useCallback(() => {
    broadcast({ type: 'logout' });
  }, [broadcast]);

  /**
   * Broadcast session extend event to other tabs.
   */
  const broadcastExtend = useCallback(() => {
    broadcast({ type: 'extend' });
  }, [broadcast]);

  /**
   * Broadcast warning state to other tabs.
   */
  const broadcastWarning = useCallback(
    (timeRemaining: number) => {
      broadcast({ type: 'warning', timeRemaining });
    },
    [broadcast],
  );

  /**
   * Register callback for activity events from other tabs.
   */
  const onActivityReceived = useCallback((callback: (timestamp: number) => void) => {
    activityCallbackRef.current = callback;
  }, []);

  /**
   * Register callback for logout events from other tabs.
   */
  const onLogoutReceived = useCallback((callback: () => void) => {
    logoutCallbackRef.current = callback;
  }, []);

  /**
   * Register callback for warning events from other tabs.
   */
  const onWarningReceived = useCallback((callback: (timeRemaining: number) => void) => {
    warningCallbackRef.current = callback;
  }, []);

  return {
    broadcastActivity,
    broadcastLogout,
    broadcastExtend,
    broadcastWarning,
    onActivityReceived,
    onLogoutReceived,
    onWarningReceived,
  };
};
